import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { PolicyDetailComponent } from '../policy-detail.component';
import { LoadingService } from '@core/services/loading.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CurrencyFormatPipe } from '@core/pipes/currency-format.pipe';
import { DateFormatPipe } from '@core/pipes/date-format.pipe';
import { AlertService } from '@core/services/alert.service';
import { TranslateService } from '@ngx-translate/core';
import { CorrespondenceService } from '@core/services/correspondence.service';
import { PolicyService } from '@core/services/policy.service';
import { DataService } from '@core/services/data.service';
import { LOB } from '@shared/models/LOB';
import { CommonService } from '@core/services/common.service';
import { B2bService } from '@core/services/b2b.service';
import moment from 'moment';
import { DocType } from '@shared/models/doc-type.enum';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FileItem, NxFileUploaderComponent } from '@aposin/ng-aquila/file-uploader';
import { UploadService } from '@core/services/upload.service';
import { UUID } from 'uuid-generator-ts';
import { NxTabChangeEvent } from '@aposin/ng-aquila/tabs';
import { B2bFilterType } from '@shared/models/b2b-filter-type.enum';
import { PartnerDocument } from '@shared/models/partner-document';
import { ModalService } from '@core/services/modal.service';
import { SortEvent, SortDirection } from '@aposin/ng-aquila/table';

export class CustomFileItem extends FileItem {
    docType!: string;
    identifier!: string;
    errors?: {
        [key: string]: any;
    };
}

export type UploadedFile = {
    identifier: string;
    name: string;
    size: number;
};

export type DocumentResponse = {
    errors: {
        [identifier: string]: {
            [key: string]: any;
        };
    },
    documents: Array<UploadedFile>,
};

@Component({
    selector: 'app-policy-detail-b2b-lease',
    templateUrl: './policy-detail-b2b-lease.component.html',
    styleUrls: ['./policy-detail-b2b-lease.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class PolicyDetailB2bLeaseComponent extends PolicyDetailComponent implements OnInit {

    public showMaInsurance = false;
    public LOB = new LOB();
    public documentsTabIndex: number = 0;

    public uploadForm: FormGroup;
    public filterForm: FormGroup;

    public filterType = B2bFilterType;
    private currentDocType = DocType.toDownload;

    public partnerDocumentList: PartnerDocument[] = [];

    // table
    public page = 1;
    public elementsPerPage = 10;
    public partnerDocumentListPage: PartnerDocument[] = [];

    @ViewChild('documentUpload', { static: false })
    documentUpload!: NxFileUploaderComponent;

    constructor(
        public commonService: CommonService,
        protected dataService: DataService,
        protected policyService: PolicyService,
        protected route: ActivatedRoute,
        protected correspondenceService: CorrespondenceService,
        protected translateService: TranslateService,
        protected alertService: AlertService,

        private dateFormatPipe: DateFormatPipe,
        private currencyFormatPipe: CurrencyFormatPipe,
        public b2b: B2bService,
        public router: Router,
        private uploadService: UploadService,
        protected loadingService: LoadingService,
        private modalService: ModalService,
    ) {
        super(commonService, dataService, policyService, route, correspondenceService, translateService, alertService, loadingService);

    }

    ngOnInit(): void {
        super.ngOnInit();
        this.policyService.getPolicyDetail(this.policyState, this.policyNumber).subscribe(res => {
            this.policy = res.policy;
            this.maInsurance = res.maInsurance;

            this.downLoadDocumentUrl = this.dataService.environmentConfig.downloadDocumentUrl;
            this.uploadForm = new FormGroup({
                policyNumber: new FormControl({
                    value: this.policyNumber,
                    disabled: true
                }),
                type: new FormControl({
                    value: 'Nová prihláška k poisteniu',
                    disabled: true
                }),
                category: new FormControl({
                    value: this.b2b.getProductName(this.policy),
                    disabled: true
                }),
                note: new FormControl(null, Validators.maxLength(32)),
                document: new FormControl()
            });

            this.uploadForm.controls.note.valueChanges.subscribe((value: string) => {
                if (value.length > 32) {
                    this.uploadForm.controls.note.setValue(value.slice(0, 32));
                }
            });

            this.filterForm = new FormGroup({
                dateFrom: new FormControl(moment(moment.now()).subtract(30, 'days')),
                dateTo: new FormControl(moment(moment.now())),
                type: new FormControl(B2bFilterType.all),
                policyNumber: new FormControl({
                    value: this.policyNumber,
                    disabled: true
                })
            });

            this.loadDocuments();
        });
    }

    public filterDocuments() {
        this.loadDocuments();
    }

    public loadDocuments() {
        this.b2b.getPartnerDocumentList(
            this.policyNumber,
            this.filterForm.controls.dateFrom.value,
            this.filterForm.controls.dateTo.value,
            this.filterForm.controls.type.value,
            this.currentDocType
        ).subscribe((result) => {
            this.partnerDocumentList = result;
            this.sortTable(new SortEvent('createTs', 'desc'));
        });
    }

    public documentsTabChanged(tabChange: NxTabChangeEvent) {
        this.documentsTabIndex = tabChange.index;
        this.currentDocType = this.documentsTabIndex == 0 ? DocType.toDownload : DocType.sentToASP;

        this.loadDocuments();
        this.page = 1;
    }

    public uploadFiles() {
        const formData = new FormData();
        Object.keys(this.uploadForm.controls).forEach(k => {
            if (k == 'document') {
                return;
            }
            formData.append(k, this.uploadForm.get(k).value);
        });

        this.documentUpload.value.forEach((fileItem: CustomFileItem) => {
            if (!fileItem.isUploaded) {
                fileItem.identifier = (new UUID()).toString();
                fileItem.setUploadingState();
                formData.append(
                    'documents[]',
                    fileItem.file as Blob,
                    fileItem.name,
                );
                formData.append(
                    'documents[][identifier]',
                    fileItem.identifier,
                );
            }
        });

        this.uploadService.doUpload(this.commonService.getServiceUrl('partner-document/upload'), formData)
            .subscribe((response: DocumentResponse) => {
                this.documentUpload.value!.forEach((fileItem: FileItem) => {
                    const cfi: CustomFileItem = fileItem as CustomFileItem;
                    if (response.errors && response.errors[cfi.identifier] !== undefined) {
                        cfi.errors = response.errors[cfi.identifier];
                        fileItem.setErrorState();
                    } else {
                        fileItem.setUploadedState();
                    }
                });
            });
    }

    documentDownloadUrl(policyNumber: string, itemId: string) {
        return this.commonService.getServiceUrl(`partner-document/content/${policyNumber}/${itemId}`);
    }

    showParts(partnerDocument: PartnerDocument) {
        this.modalService.openModal('PartnerDocumentPart', {
            partnerDocument: partnerDocument,
        });
    }

    updatePage() {
        const indexMin = (this.page - 1) * this.elementsPerPage;
        const indexMax = indexMin + this.elementsPerPage;
        this.partnerDocumentListPage =
            this.partnerDocumentList.filter(
                (x, index) => index >= indexMin && index < indexMax,
            );
    }

    prevPage() {
        this.page--;
        this.updatePage();
    }

    nextPage() {
        this.page++;
        this.updatePage();
    }

    goToPage(n: number) {
        this.page = n;
        this.updatePage();
    }

    sortTable(sort: SortEvent) {
        this.partnerDocumentList = this.partnerDocumentList.sort(
            (a: { [key: string]: any; }, b: { [key: string]: any; }) => {
                if (sort.active in a && sort.active in b) {
                    if (a[sort.active] === null) {
                        a[sort.active] = '';
                    }
                    if (b[sort.active] === null) {
                        b[sort.active] = '';
                    }
                    return this.compare(
                        a[sort.active],
                        b[sort.active],
                        sort.direction,
                    );
                }
                return 0;
            },
        );

        this.updatePage();
    }

    compare(
        a: number | string | Date,
        b: number | string | Date,
        direction: SortDirection,
    ) {
        if (a < b) {
            return direction === 'asc' ? -1 : 1;
        } else if (a > b) {
            return direction === 'asc' ? 1 : -1;
        }
        return 0;
    }
}